import * as pdfjsLib from 'pdfjs-dist';
import { createWorker } from 'tesseract.js';
import mammoth from 'mammoth';
import { supabase } from '@/integrations/supabase/client';

// Disable worker to avoid loading issues in some environments
pdfjsLib.GlobalWorkerOptions.workerSrc = '';

export interface DocumentProcessingProgress {
  stage: 'loading' | 'extracting-text' | 'ocr-processing' | 'finalizing' | 'converting';
  progress: number;
  message: string;
}

export interface DocumentProcessingResult {
  text: string;
  processingMethod: 'text-extraction' | 'ocr' | 'docx-extraction';
  pageCount?: number;
}

export class DocumentProcessor {
  
  static async processDocument(
    file: File, 
    onProgress?: (progress: DocumentProcessingProgress) => void
  ): Promise<DocumentProcessingResult> {
    const fileType = file.type;
    
    onProgress?.({
      stage: 'loading',
      progress: 10,
      message: 'Loading document...'
    });

    try {
      if (fileType === 'application/pdf') {
        return await this.processPDF(file, onProgress);
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileType === 'application/msword'
      ) {
        return await this.processWordDocument(file, onProgress);
      } else {
        throw new Error('Unsupported file type');
      }
    } catch (error) {
      console.error('Document processing error:', error);
      throw new Error(`Failed to process document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private static async processPDF(
    file: File, 
    onProgress?: (progress: DocumentProcessingProgress) => void
  ): Promise<DocumentProcessingResult> {
    
    onProgress?.({
      stage: 'converting',
      progress: 20,
      message: 'Uploading PDF for processing...'
    });

    // Simple approach: Upload file and use Firecrawl to extract ALL text
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploaded-documents')
        .upload(fileName, file);

      if (uploadError) {
        throw new Error('Failed to upload file');
      }

      onProgress?.({
        stage: 'converting',
        progress: 60,
        message: 'Extracting all text from PDF...'
      });

      const { data: { publicUrl } } = supabase.storage
        .from('uploaded-documents')
        .getPublicUrl(fileName);

      // Use scrape-job-url which uses Firecrawl to extract ALL text
      const { data, error } = await supabase.functions.invoke('scrape-job-url', {
        body: { url: publicUrl }
      });

      // Clean up temp file
      supabase.storage.from('uploaded-documents').remove([fileName]);

      if (error || !data?.content) {
        throw new Error('Failed to extract text from PDF');
      }

      onProgress?.({
        stage: 'finalizing',
        progress: 100,
        message: 'Text extraction completed!'
      });

      console.log('Firecrawl extracted text preview:', data.content.substring(0, 200));

      return {
        text: data.content.trim(),
        processingMethod: 'text-extraction'
      };

    } catch (error) {
      console.error('PDF processing failed:', error);
      throw new Error(`Failed to process PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private static async processWordDocument(
    file: File, 
    onProgress?: (progress: DocumentProcessingProgress) => void
  ): Promise<DocumentProcessingResult> {
    
    onProgress?.({
      stage: 'extracting-text',
      progress: 30,
      message: 'Processing Word document...'
    });

    const arrayBuffer = await file.arrayBuffer();
    
    onProgress?.({
      stage: 'extracting-text',
      progress: 60,
      message: 'Extracting text from Word document...'
    });

    const result = await mammoth.extractRawText({ arrayBuffer });
    
    if (result.messages.length > 0) {
      console.warn('Word document processing warnings:', result.messages);
    }
    
    // Return ALL Word text with zero cleaning
    const cleanedText = result.value.trim();
    
    onProgress?.({
      stage: 'finalizing',
      progress: 100,
      message: 'Word document processed successfully!'
    });

    return {
      text: cleanedText,
      processingMethod: 'docx-extraction'
    };
  }

  static getSupportedFileTypes(): string[] {
    return [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
  }

  static getFileTypeDescription(type: string): string {
    switch (type) {
      case 'application/pdf':
        return 'PDF Document';
      case 'application/msword':
        return 'Word Document (.doc)';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word Document (.docx)';
      default:
        return 'Unknown Document Type';
    }
  }
}